import { get } from 'lodash';
import { memo, type ReactElement, useCallback } from 'react';
import {
    type Control,
    Controller,
    type FieldError,
    type FieldErrors,
    type FieldValues,
    type Mode,
    type Path,
    type UseFormRegister,
} from 'react-hook-form';
import type {
    NativeSyntheticEvent,
    TextInputFocusEventData,
} from 'react-native';
import {
    type GetProps,
    type Input as BaseInput,
    type StackProps,
    type TextProps,
    XStack,
    YStack,
} from 'tamagui';

import { Input } from '../inputs/Input';
import { InputTrigger } from '../inputs/InputTrigger';
import type { StyledInput } from '../inputs/StyledInputs';
import {
    BodySmallEmphasized,
    FootnoteRegular,
    LabelRegular,
} from '../text/Typography';

export const LABEL_TEXT_COLOR = '$black';
export const ERROR_TEXT_COLOR = '$red1100';
export const HINT_TEXT_COLOR = '$gray1000';
export const ERROR_HINT_TEXT_COLOR = '$gray1000';

export const USE_FORM_MODE_DEFAULTS: {
    mode: Mode;
    delayError: number;
} = {
    mode: 'onChange',
    delayError: 1000,
};

export interface BaseControlledProps<
    TFieldValues extends FieldValues,
    TContext,
> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
}

interface ControlledTextInputProps<
    TFieldValues extends FieldValues,
    TContext,
> extends GetProps<typeof BaseInput> {
    Input: StyledInput;
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    errorMessageProps?: TextProps;
    type?: 'text' | 'numeric';
    label?: string;
    formatValueOnChange?: (previousValue: string, nextValue: string) => string;
    labelStyle?: TextProps;
    containerStyle?: StackProps;
}

/** @deprecated: Seems to duplicate ControlledInput, but older and fewer uses */
export const ControlledTextInput = <
    TFieldValues extends FieldValues,
    TContext,
>({
    Input,
    name,
    control,
    register,
    label,
    labelStyle,
    value,
    errors,
    type = 'text',
    formatValueOnChange,
    containerStyle,
    errorMessageProps,
    ...props
}: ControlledTextInputProps<TFieldValues, TContext>) => {
    // Use lodash get to access nested values in dot notation
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...register,
            }}
            render={({ field: { onChange, ref, value } }) => (
                <YStack gap={12} {...containerStyle}>
                    {label != null && (
                        <BodySmallEmphasized
                            color={
                                isError ? ERROR_TEXT_COLOR : LABEL_TEXT_COLOR
                            }
                            textAlign="center"
                            {...labelStyle}
                        >
                            {label}
                        </BodySmallEmphasized>
                    )}
                    {/* @ts-expect-error - TODO tamagui upgrade: fontWeight tokens are required and should be valid */}
                    <Input
                        ref={ref}
                        value={value}
                        invalid={isError}
                        onChangeText={(text: string) => {
                            onChange(
                                formatValueOnChange?.(value, text) ?? text,
                            );
                        }}
                        borderBottomWidth={1}
                        textAlign="left"
                        keyboardType={
                            type === 'numeric' ? 'numeric' : 'default'
                        }
                        {...props}
                    />
                    {isError && (
                        <FootnoteRegular
                            color={ERROR_TEXT_COLOR}
                            {...errorMessageProps}
                        >
                            {error?.message}
                        </FootnoteRegular>
                    )}
                </YStack>
            )}
        />
    );
};

export const ControlledInputWrapper = memo(
    ({
        rightSideContent,
        children,
    }: {
        rightSideContent?: React.ReactNode;
        children: ReactElement;
    }) => {
        return rightSideContent != null ? (
            <XStack alignItems="center" gap={8}>
                {children}
                {rightSideContent}
            </XStack>
        ) : (
            children
        );
    },
);

export interface ControlledInputProps<
    TFieldValues extends FieldValues,
    TContext,
> extends GetProps<typeof Input> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    type?: 'text' | 'numeric';
    label?: string;
    hint?: string;
    formatValueOnChange?: (previousValue: string, nextValue: string) => string;
    forceFocusStyle?: boolean;
    containerStyle?: StackProps;
    rightSideContent?: React.ReactNode;
}

export const ControlledInput = <TFieldValues extends FieldValues, TContext>({
    name,
    control,
    register,
    label,
    hint,
    value,
    errors,
    onFocus,
    type = 'text',
    formatValueOnChange,
    forceFocusStyle,
    containerStyle,
    rightSideContent,
    ...props
}: ControlledInputProps<TFieldValues, TContext>) => {
    // Use lodash get to access nested values in dot notation
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    const handleFocus = useCallback(
        (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
            e.preventDefault();
            onFocus?.(e);
        },
        [onFocus],
    );

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...register,
            }}
            render={({ field: { onChange, ref, onBlur, value } }) => (
                <YStack gap={8} {...containerStyle}>
                    {label != null && (
                        <LabelRegular
                            color={
                                isError ? ERROR_TEXT_COLOR : LABEL_TEXT_COLOR
                            }
                        >
                            {label}
                        </LabelRegular>
                    )}
                    {hint != null && (
                        <LabelRegular
                            color={
                                isError
                                    ? ERROR_HINT_TEXT_COLOR
                                    : HINT_TEXT_COLOR
                            }
                        >
                            {hint}
                        </LabelRegular>
                    )}
                    <ControlledInputWrapper rightSideContent={rightSideContent}>
                        <Input
                            ref={ref}
                            onBlur={onBlur}
                            onFocus={handleFocus}
                            invalid={isError}
                            value={value}
                            onChangeText={(text: string) => {
                                onChange(
                                    formatValueOnChange?.(value, text) ?? text,
                                );
                            }}
                            {...props}
                        />
                    </ControlledInputWrapper>
                    {isError && (
                        <FootnoteRegular color={ERROR_TEXT_COLOR}>
                            {error?.message}
                        </FootnoteRegular>
                    )}
                </YStack>
            )}
        />
    );
};

interface ControlledInputTriggerProps<
    TFieldValues extends FieldValues,
    TContext,
> extends GetProps<typeof InputTrigger> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    formatValue?: (value: string) => string;
    label?: string;
}

export const ControlledInputTrigger = <
    TFieldValues extends FieldValues,
    TContext,
>({
    name,
    control,
    register,
    label,
    value,
    formatValue,
    errors,
    ...props
}: ControlledInputTriggerProps<TFieldValues, TContext>) => {
    // Use lodash get to access nested values in dot notation
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...register,
            }}
            render={({ field: { ref, onBlur, value } }) => (
                <YStack gap={8}>
                    {label != null && (
                        <LabelRegular
                            color={
                                isError ? ERROR_TEXT_COLOR : LABEL_TEXT_COLOR
                            }
                        >
                            {label}
                        </LabelRegular>
                    )}
                    <InputTrigger
                        ref={ref}
                        onBlur={onBlur}
                        invalid={isError}
                        value={formatValue ? formatValue(value) : value}
                        {...props}
                    />
                    {isError && (
                        <FootnoteRegular color={ERROR_TEXT_COLOR}>
                            {error?.message}
                        </FootnoteRegular>
                    )}
                </YStack>
            )}
        />
    );
};

export const ControlledMultilineInput = <
    TFieldValues extends FieldValues,
    TContext,
>({
    numberOfLines = 3,
    ...props
}: ControlledInputProps<TFieldValues, TContext>) => {
    return (
        <ControlledInput
            {...props}
            height={numberOfLines * 22 + 32} // approx line height + vertical padding
            fontWeight="regular"
            numberOfLines={numberOfLines}
            multiline
        />
    );
};
